import type { Address } from '@my/directory-api';
import { geocodeAddress, type GeocodedAddress } from './geocodeAddress';

export type AddressStatus = 'matched' | 'new' | 'failed';

/**
 * Outcome of resolving one free-text address:
 *  - `matched` — geocoded to a place id that already exists as an org address,
 *    so we reuse `existingId` and create nothing.
 *  - `new` — geocoded to a place id we don't have yet; `geocoded` is created
 *    once at commit (deduped by place id across the batch).
 *  - `failed` — couldn't geocode to a real place; its order(s) can't import.
 */
export interface AddressResolution {
  text: string;
  status: AddressStatus;
  existingId?: string;
  geocoded?: GeocodedAddress;
  placeId?: string;
  error?: string;
}

/** Index existing org locations by place id (skipping blanks) for dedup. */
export function buildPlaceIdIndex(locations: Address[]): Map<string, string> {
  const index = new Map<string, string>();
  for (const a of locations) {
    if (a.placeId) index.set(a.placeId, a.id);
  }
  return index;
}

/** Geocode one address and classify it against the existing-location index. */
async function resolveOne(
  geocoder: google.maps.Geocoder,
  text: string,
  existingByPlaceId: Map<string, string>,
): Promise<AddressResolution> {
  const geo = await geocodeAddress(geocoder, text);
  if (!geo) return { text, status: 'failed', error: 'Could not geocode this address' };

  const existingId = existingByPlaceId.get(geo.placeId);
  if (existingId) return { text, status: 'matched', existingId, placeId: geo.placeId };

  return { text, status: 'new', geocoded: geo, placeId: geo.placeId };
}

/** Bounded-concurrency map — workers swallow their own errors. */
async function runPool<T>(items: T[], worker: (item: T) => Promise<void>, concurrency = 4): Promise<void> {
  let cursor = 0;
  const lane = async () => {
    while (cursor < items.length) {
      const i = cursor++;
      await worker(items[i]!);
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, lane));
}

/**
 * Geocode every distinct address and classify each against existing org
 * locations. Returns a `text → resolution` map; the commit step turns `new`
 * ones into addresses (deduped again by place id) and reuses `matched` ids.
 */
export async function resolveAddresses(
  geocoder: google.maps.Geocoder,
  texts: string[],
  existingByPlaceId: Map<string, string>,
  onProgress?: (done: number, total: number) => void,
): Promise<Map<string, AddressResolution>> {
  const out = new Map<string, AddressResolution>();
  let done = 0;
  onProgress?.(0, texts.length);
  await runPool(texts, async (text) => {
    const res = await resolveOne(geocoder, text, existingByPlaceId);
    out.set(text, res);
    done += 1;
    onProgress?.(done, texts.length);
  });
  return out;
}
